import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { useSessionStore } from '../store/auth/auth.store'
import {
  Image as ImageIcon,
  Plus,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { GlobalFilterBar } from '../components/ui/GlobalFilterBar'
import { TableSkeleton } from '../components/ui/table-skeleton'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { promotionSectionService, StorePromotionSection } from '../services/promotion-section.service'
import { PromotionSectionModal } from '../components/promotions/PromotionSectionModal'

export function PromotionSections() {
  const canCreate = useSessionStore((state) =>
    state.canAction('configuration.configuration.create')
  )
  const canEdit = useSessionStore((state) =>
    state.canAction('configuration.configuration.edit')
  )
  const canChangeStatus = useSessionStore((state) =>
    state.canAction('configuration.configuration.status')
  )
  const canDelete = useSessionStore((state) =>
    state.canAction('configuration.configuration.delete')
  )
  const [sections, setSections] = useState<StorePromotionSection[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')

  // Paginación
  const [pagination, setPagination] = useState({
    currentPage: 1,
    total: 0,
    perPage: 10,
    lastPage: 1,
    from: 0,
    to: 0,
  })

  // Modals state
  const [showModal, setShowModal] = useState(false)
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)
  const [statusConfirmId, setStatusConfirmId] = useState<number | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)

  const fetchSections = async (page = 1, search = searchQuery) => {
    setIsLoading(true)
    try {
      const res = await promotionSectionService.getSections({
        page,
        per_page: pagination.perPage,
        name: search,
      })
      const paginator = res.data

      setSections(paginator.data)
      setPagination({
        currentPage: paginator.current_page,
        total: paginator.total,
        perPage: paginator.per_page,
        lastPage: paginator.last_page,
        from: paginator.from,
        to: paginator.to,
      })
    } catch (error) {
      toast.error('Error al cargar las secciones de promociones')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchSections(pagination.currentPage, searchQuery)
  }, [pagination.currentPage, searchQuery])

  const openCreateModal = () => {
    setEditingId(null)
    setShowModal(true)
  }

  const openEditModal = (section: StorePromotionSection) => {
    setEditingId(section.id)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingId(null)
  }

  const executeDelete = async () => {
    if (!deleteConfirmId) return
    try {
      await promotionSectionService.deleteSection(deleteConfirmId)
      toast.success('Sección eliminada exitosamente')
      fetchSections()
    } catch (error) {
      toast.error('Error', { description: 'No se pudo eliminar la sección.' })
    } finally {
      setDeleteConfirmId(null)
    }
  }

  const executeChangeStatus = async () => {
    if (!statusConfirmId) return
    try {
      await promotionSectionService.toggleSectionStatus(statusConfirmId)
      toast.success('Estado de la sección actualizado exitosamente')
      fetchSections()
    } catch (error) {
      toast.error('Error', { description: 'No se pudo actualizar el estado de la sección.' })
    } finally {
      setStatusConfirmId(null)
    }
  }

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString()
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header & Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Secciones de Promociones (4 Cards)</h1>
          <p className="mt-1 text-sm text-slate-600">
            Administra los bloques de 4 promociones de la tienda en línea.
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          {canCreate && (
            <Button
              onClick={openCreateModal}
              className="w-full bg-primary hover:bg-primary/90 sm:w-auto"
            >
              <Plus className="mr-2 h-4 w-4" />
              Nueva Sección
            </Button>
)}
        </div>
      </div>
      <GlobalFilterBar
        show={['search']}
        values={{ search: searchQuery }}
        onChange={(changes) => {
          if (changes.search !== undefined) {
            setSearchQuery(changes.search)
            setPagination((prev) => ({ ...prev, currentPage: 1 }))
          }
        }}
        customOptions={{ searchPlaceholder: 'Buscar sección...' }}
      />

      {/* Table */}
      {isLoading ? (
        <TableSkeleton
          headers={['Nombre', 'Título / Subtítulo', 'Estado', 'Cards', 'Periodo', 'Acciones']}
          columns={['text', 'text', 'badge', 'text', 'text', 'actions']}
          rows={5}
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-table-bg text-table-foreground shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap">
              <thead className="border-b border-border bg-table-header-bg text-table-header-foreground">
                <tr className="transition-colors hover:bg-table-row-hover">
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Título / Subtítulo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Cards
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Periodo
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-table-bg text-table-foreground">
                {sections.length > 0 ? (
                  sections.map((section) => (
                    <tr key={section.id} className="hover:bg-table-row-hover">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <p className="font-medium text-table-foreground">{section.name}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">{section.title || '-'}</span>
                          <span className="text-xs text-slate-500">{section.subtitle || '-'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge
                          className={
                            section.is_active
                              ? 'bg-[#DCFCE6] text-[#008438]'
                              : 'bg-[#FFE2E2] text-[#C10008]'
                          }
                        >
                          {section.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-sm text-table-foreground">
                        {section.items?.length || 0}
                      </td>
                      <td className="px-6 py-4 text-sm text-table-foreground">
                        {section.starts_at || section.ends_at
                          ? `${formatDate(section.starts_at)} - ${formatDate(section.ends_at)}`
                          : 'Indefinido'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {canChangeStatus && (
                            <button
                              onClick={() => setStatusConfirmId(section.id)}
                              title={section.is_active ? 'Desactivar' : 'Activar'}
                              className={`rounded p-1.5 transition-colors ${
                                section.is_active
                                  ? 'text-primary hover:bg-primary/10'
                                  : 'text-table-foreground hover:bg-muted'
                              }`}
                            >
                              {section.is_active ? (
                                <ToggleRight className="h-5 w-5" />
                              ) : (
                                <ToggleLeft className="h-5 w-5" />
                              )}
                            </button>
                          )}

                          {canEdit && (
                            <button
                              onClick={() => openEditModal(section)}
                              title="Editar"
                              className="rounded p-1.5 text-table-foreground transition-colors hover:bg-muted"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}

                          {canDelete && (
                            <button
                              onClick={() => setDeleteConfirmId(section.id)}
                              title="Eliminar"
                              className="rounded p-1.5 text-red-600 transition-colors hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <ImageIcon className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                      <p className="text-slate-500">No se encontraron secciones</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {sections.length > 0 && (
            <GlobalPaginator
              totalItems={pagination.total}
              itemsPerPage={pagination.perPage}
              currentPage={pagination.currentPage}
              from={pagination.from}
              to={pagination.to}
              onPageChange={(page) =>
                setPagination((prev) => ({ ...prev, currentPage: page }))
              }
            />
          )}
        </div>
      )}

      {showModal && (
        <PromotionSectionModal
          open={showModal}
          onOpenChange={handleCloseModal}
          sectionId={editingId}
          onSuccess={() => {
            fetchSections(pagination.currentPage)
            handleCloseModal()
          }}
        />
      )}

      <ConfirmationModal
        isOpen={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={executeDelete}
        preset="delete"
        itemName="sección"
      />
      <ConfirmationModal
        isOpen={statusConfirmId !== null}
        onClose={() => setStatusConfirmId(null)}
        onConfirm={executeChangeStatus}
        preset="toggleStatus"
        itemName="sección"
      />
    </div>
  )
}
